/** @jsxImportSource @emotion/react */
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from 'react-redux';
import {Button, Card, Modal, Table, Tooltip} from 'antd';
import Flex from "../../../../components/shared-components/Flex";
import {DeleteOutlined, EditOutlined, ExclamationCircleOutlined} from "@ant-design/icons";
import {
    deleteCategory,
    fetchCategoriesByFilter,
    setFilters,
    setPagination,
    setSorter
} from 'store/slices/categoriesSlice';
import CategoryModal from "./CategoryModal";
import CategoryFilter from "./CategoryFilter";
import IntlMessage from "../../../../components/util-components/IntlMessage";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

const CategoryComponent = () => {
    const dispatch = useDispatch();
    const {data, pagination, loading, sorter, filters} = useSelector((state) => state.categories);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const {t} = useTranslation();
    const {confirm} = Modal;
    const navigate = useNavigate();

    useEffect(() => {
        dispatch(fetchCategoriesByFilter({page: pagination.current, pageSize: pagination.pageSize, sorter, filters, t})); // Fetch categories on mount
    }, [dispatch, pagination.current, pagination.pageSize, sorter, filters]);

    const handleCategoryClick = (category) => {
        navigate(`/app/category/${category.id}/links`);
    };

    const handleTableChange = (pagination, _, sorter) => {
        const sortData = sorter.order
            ? {field: sorter.field, order: sorter.order}
            : {field: null, order: null};

        dispatch(setPagination({current: pagination.current, pageSize: pagination.pageSize}));
        dispatch(setSorter(sortData));
    };

    // Open modal for adding
    const openAddModal = () => {
        setSelectedCategory(null); // Clear selection
        setIsModalVisible(true);
    };

    // Open modal for editing
    const openEditModal = (category) => {
        console.log("Category details: ", category);
        setSelectedCategory(category); // Set category to edit
        setIsModalVisible(true);
    };

    // Close modal
    const closeModal = () => {
        setIsModalVisible(false);
        setSelectedCategory(null); // Reset selection
    };

    // Handle deleting a category
    const handleDeleteCategory = (elm) => {
        const content = <p>{t("name")}: {elm.title}</p>
        confirm({
            title: <IntlMessage id="deleteCategoryConfirm"/>,
            icon: <ExclamationCircleOutlined/>,
            content: content,
            okText: <IntlMessage id="yes"/>,
            okType: 'danger',
            cancelText: <IntlMessage id="no"/>,
            onOk() {
                dispatch(deleteCategory({id: elm.id, t: t}));
            }
        });
    };

    // Handle filter search
    const handleSearch = (newFilters) => {
        dispatch(setFilters(newFilters));
        dispatch(setPagination({current: 1, pageSize: pagination.pageSize}));
    };

    const columns = [
        {
            title: <IntlMessage id="name"/>,
            dataIndex: "title",
            key: "name",
            sorter: true,
            render: (text, record) => (
                <a onClick={() => handleCategoryClick(record)}>{text}</a>
            )

        },
        {
            title: <IntlMessage id="description"/>,
            dataIndex: "description",
            key: "description",
            render: description => <p>{description}</p>
        },
        {
            title: <IntlMessage id="owner"/>,
            dataIndex: "owner",
            width: 300,
            key: "owner",
            render: (owner) => {
                return owner?.personDetails ? `${owner.personDetails.firstName} ${owner.personDetails.lastName}` : 'N/A';
            }
        },
        {
            title: <IntlMessage id="actions"/>,
            dataIndex: 'actions',
            width: 100,
            render: (_, elm) => (
                <div className="text-right d-flex justify-content-end">
                    <Tooltip title={t("edit")}>
                        <Button type="primary" className="mr-2" icon={<EditOutlined/>}
                                onClick={() => openEditModal(elm)} size="small"/>
                    </Tooltip>
                    <Tooltip title={t("delete")}>
                        <Button danger icon={<DeleteOutlined/>} onClick={() => handleDeleteCategory(elm)}
                                size="small"/>
                    </Tooltip>
                </div>
            )
        }
    ];

    return (
        <Card>
            <Flex className="mb-1" alignItems="center" justifyContent="center" mobileFlex={false}>
                <CategoryFilter onSearch={handleSearch} onAdd={openAddModal}/>
            </Flex>
            <CategoryModal isVisible={isModalVisible} onClose={closeModal} category={selectedCategory}
                           width={window.innerWidth < 768 ? "90%" : 600}/>
            <Table columns={columns}
                   dataSource={data}
                   rowKey='id'
                   loading={loading}
                   pagination={{
                       current: pagination.current,
                       pageSize: pagination.pageSize,
                       total: pagination.total,
                   }}
                   onChange={handleTableChange}
            />
        </Card>
    );
}

export default CategoryComponent;
